import './optionSelectorBooking.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, ButtonVariant, Collapse, Modal, Size } from '@eliberty/design-system-react';

import { OPTION_NOT_FOR_EVERYONE, ResponsiveSize, SELECTOR_MODE } from '~constants';
import {
  addOptionTimeslotToConcernedParticipants,
  addOptionToParticipants,
  findModalPortal,
  getParticipantsWithOption,
} from '~helpers';
import { useMediaQuery, useProductBySlug, useTimeslots } from '~hooks';
import { ParticipantsPicker } from '~molecules';
import DateSelector from '~organisms/DateSelector/dateSelector';
import { AvailableOption, Timeslot } from '~types/appsync.types';
import { Participant } from '~types/participant';
import { extractTime, formatTimeslotDateRange, parseCalendarDate } from '~utils';

import OptionSelectorContainer from '../OptionSelectorContainer/optionSelectorContainer';

const formatSummaryDate = (date: string, locale: string) => {
  return parseCalendarDate(date).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
  });
};

/**
 * Returns the single available daily timeslot if auto-booking conditions are met.
 * Auto-booking requires exactly 1 available timeslot that is a daily booking.
 */
const getAutoBookableTimeslot = (timeslots: Timeslot[]): Timeslot | null => {
  const availableTimeslots = timeslots.filter((slot) => slot.quotaAvailable > 0);
  if (availableTimeslots.length === 1 && availableTimeslots[0].dailyBooking) {
    return availableTimeslots[0];
  }
  return null;
};

interface OptionSelectorBookingProps {
  date: Date;
  option: AvailableOption;
  participants: Participant[];
  currency: string;
  participantMode: SELECTOR_MODE;
  onParticipantsChange: (participants: Participant[]) => void;
  /** When true, renders booking content inline (Collapse) instead of a modal */
  inlineBooking?: boolean;
}

const OptionSelectorBooking = ({
  date,
  option,
  participants,
  currency,
  participantMode,
  onParticipantsChange,
  inlineBooking = false,
}: OptionSelectorBookingProps) => {
  const intl = useIntl();
  const isMobile = useMediaQuery(ResponsiveSize.SCREEN_S_MOBILE);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditingInline, setIsEditingInline] = useState(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot | undefined>(undefined);

  // Local state for modal editing (only applied on confirm)
  const [localParticipants, setLocalParticipants] = useState<Participant[]>(participants);
  const [localTimeslot, setLocalTimeslot] = useState<Timeslot | undefined>(undefined);

  // Sync localParticipants when participants prop changes (and modal is closed)
  useEffect(() => {
    if (!isModalOpen) {
      setLocalParticipants(participants);
    }
  }, [participants, isModalOpen]);

  const [product] = useProductBySlug(option.slug);

  const participantsWithOption = getParticipantsWithOption(participants, option.slug);
  const localParticipantsWithOption = getParticipantsWithOption(localParticipants, option.slug);
  const isOptionSelected = participantsWithOption.length > 0;

  // Derived option configuration
  const isMandatory = option.mandatory;
  const isNotForEveryone = option.tags.includes(OPTION_NOT_FOR_EVERYONE);
  const hasSingleBookingDay = option.bookingDays.length === 1;
  const canAutoSelectParticipants = !isNotForEveryone || participants.length === 1;

  // Fetch timeslots when single booking day (for auto-booking and availability check)
  const singleDayDate = hasSingleBookingDay ? parseCalendarDate(option.bookingDays[0]) : null;
  const now = useMemo(() => new Date(), []);
  const { dateFrom, dateTo } = singleDayDate
    ? formatTimeslotDateRange(singleDayDate, now)
    : { dateFrom: undefined, dateTo: undefined };
  const [singleDayTimeslots, isLoadingTimeslots] = useTimeslots(
    hasSingleBookingDay ? option.slug : undefined,
    dateFrom,
    dateTo,
  );

  // Sync selectedTimeslot from participants and remove option if timeslot date is no longer available
  useEffect(() => {
    if (isModalOpen || participantsWithOption.length === 0) {
      return;
    }

    const existingOption = participantsWithOption[0].options.find(
      (opt) => opt.slug === option.slug,
    );
    const participantTimeslot = existingOption?.timeslot;

    // Check if the timeslot date is still available in bookingDays
    if (participantTimeslot) {
      const timeslotDate = participantTimeslot.date.split('T')[0];
      const isDateStillAvailable = option.bookingDays.some((bookingDay) => {
        const bookingDate = bookingDay.split('T')[0];
        return bookingDate === timeslotDate;
      });

      if (!isDateStillAvailable) {
        // Remove option from all participants if date is no longer available
        setSelectedTimeslot(undefined);
        const updated = addOptionToParticipants(participants, option, []);
        onParticipantsChange(updated);
        return;
      }
    }

    // Sync timeslot from participant if different
    if (participantTimeslot && participantTimeslot !== selectedTimeslot) {
      setSelectedTimeslot(participantTimeslot);
    }
  }, [
    participantsWithOption,
    option,
    isModalOpen,
    selectedTimeslot,
    participants,
    onParticipantsChange,
  ]);

  const handleOpenModal = () => {
    if (inlineBooking) {
      setIsEditingInline(true);
      return;
    }
    setLocalParticipants(participants);
    setLocalTimeslot(selectedTimeslot);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  /**
   * Attempts to auto-book the option without opening the modal.
   * Returns true if auto-booking succeeded, false otherwise.
   *
   * Auto-booking conditions:
   * - Single booking day available
   * - Exactly 1 available DAILY timeslot
   * - Can auto-select participants (not-for-everyone=false OR single participant)
   * - Enough quota for all participants
   */
  const tryAutoBook = (): boolean => {
    if (!hasSingleBookingDay || isLoadingTimeslots || singleDayTimeslots.length === 0) {
      return false;
    }

    const autoBookableTimeslot = getAutoBookableTimeslot(singleDayTimeslots);
    if (!autoBookableTimeslot) {
      return false;
    }

    if (!canAutoSelectParticipants) {
      return false;
    }

    const maxBookable = Math.min(
      autoBookableTimeslot.quotaAvailable,
      autoBookableTimeslot.limitQuotaOrderVel,
    );
    if (maxBookable < participants.length) {
      return false;
    }

    // All conditions met: auto-book all participants
    const allParticipantIds = participants.map((p) => p.id);
    let updatedParticipants = addOptionToParticipants(participants, option, allParticipantIds);
    updatedParticipants = addOptionTimeslotToConcernedParticipants(
      updatedParticipants,
      option,
      autoBookableTimeslot,
    );

    setSelectedTimeslot(autoBookableTimeslot);
    onParticipantsChange(updatedParticipants);
    return true;
  };

  /**
   * Opens the modal (or inline editor) with optional participant pre-selection.
   * Pre-selects all participants if canAutoSelectParticipants is true.
   */
  const openModalWithPreselection = () => {
    if (inlineBooking) {
      if (canAutoSelectParticipants) {
        const allParticipantIds = participants.map((p) => p.id);
        const updatedParticipants = addOptionToParticipants(
          participants,
          option,
          allParticipantIds,
        );
        onParticipantsChange(updatedParticipants);
      }
      setIsEditingInline(true);
      return;
    }

    let updatedParticipants = participants;

    if (canAutoSelectParticipants) {
      const allParticipantIds = participants.map((p) => p.id);
      updatedParticipants = addOptionToParticipants(participants, option, allParticipantIds);
    }

    setLocalParticipants(updatedParticipants);
    setLocalTimeslot(selectedTimeslot);
    setIsModalOpen(true);
  };

  const handleAddAll = () => {
    if (!tryAutoBook()) {
      openModalWithPreselection();
    }
  };

  const handleRemoveAll = () => {
    setIsModalOpen(false);
    setIsEditingInline(false);
    setSelectedTimeslot(undefined);
    setLocalTimeslot(undefined);
    const updated = addOptionToParticipants(participants, option, []);
    setLocalParticipants(updated);
    onParticipantsChange(updated);
  };

  const handleConfirm = () => {
    // Apply local changes to parent state only on confirm
    setSelectedTimeslot(localTimeslot);
    onParticipantsChange(localParticipants);
    setIsModalOpen(false);
  };

  const handleSelectParticipants = (selected: Participant[]) => {
    const selectedIds = selected.map((p) => p.id);

    const updatedParticipants = addOptionToParticipants(localParticipants, option, selectedIds);

    // Check if we need to reset the timeslot because too many participants selected
    if (
      localTimeslot &&
      selected.length > Math.min(localTimeslot.quotaAvailable, localTimeslot.limitQuotaOrderVel)
    ) {
      setLocalTimeslot(undefined);
      setLocalParticipants(updatedParticipants);
      return;
    }

    // Update local participants with current timeslot
    const updatedParticipantsWithTimeslot = addOptionTimeslotToConcernedParticipants(
      updatedParticipants,
      option,
      localTimeslot,
    );
    setLocalParticipants(updatedParticipantsWithTimeslot);
  };

  const handleSelectTimeslot = (timeslot: Timeslot | undefined) => {
    setLocalTimeslot(timeslot);

    if (timeslot) {
      const updated = addOptionTimeslotToConcernedParticipants(localParticipants, option, timeslot);
      setLocalParticipants(updated);
    }
  };

  // --- Inline booking handlers (changes propagated immediately) ---

  const handleInlineSelectParticipants = useCallback(
    (selected: Participant[]) => {
      const selectedIds = selected.map((p) => p.id);
      let updatedParticipants = addOptionToParticipants(participants, option, selectedIds);

      // Reset timeslot if too many participants selected for current quota
      if (
        selectedTimeslot &&
        selected.length >
          Math.min(selectedTimeslot.quotaAvailable, selectedTimeslot.limitQuotaOrderVel)
      ) {
        setSelectedTimeslot(undefined);
        onParticipantsChange(updatedParticipants);
        return;
      }

      updatedParticipants = addOptionTimeslotToConcernedParticipants(
        updatedParticipants,
        option,
        selectedTimeslot,
      );
      onParticipantsChange(updatedParticipants);
    },
    [participants, option, selectedTimeslot, onParticipantsChange],
  );

  const handleInlineSelectTimeslot = useCallback(
    (timeslot: Timeslot | undefined) => {
      setSelectedTimeslot(timeslot);

      if (timeslot) {
        const updated = addOptionTimeslotToConcernedParticipants(participants, option, timeslot);
        onParticipantsChange(updated);
        setIsEditingInline(false);
      }
    },
    [participants, option, onParticipantsChange],
  );

  // Build summary for collapsed view (e.g., "sam. 22 févr. - 10:00")
  const buildSummary = (): string | undefined => {
    if (participantsWithOption.length === 0 || !selectedTimeslot) {
      return undefined;
    }

    const locale = intl.locale;
    const parts: string[] = [];

    const formattedDate = formatSummaryDate(selectedTimeslot.date, locale);
    parts.push(formattedDate);

    if (!selectedTimeslot.dailyBooking) {
      const time = extractTime(selectedTimeslot.date);
      parts.push(time);
    }

    return parts.join(' - ');
  };

  const summary = buildSummary();

  const includeDates = useMemo(
    () => option.bookingDays.map(parseCalendarDate),
    [option.bookingDays],
  );

  useEffect(() => {
    if (isMandatory) {
      handleAddAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Early return: hide option if single booking day has no available timeslots
  if (hasSingleBookingDay) {
    if (isLoadingTimeslots) {
      return null;
    }
    const hasAvailableTimeslots = singleDayTimeslots.some((slot) => slot.quotaAvailable > 0);
    if (!hasAvailableTimeslots) {
      return null;
    }
  }

  // When editing inline, force expanded view by hiding summary
  const effectiveSummary = isEditingInline ? undefined : summary;
  const isInlineContentVisible =
    inlineBooking && (isEditingInline || (isOptionSelected && !summary));

  const renderBookingContent = (config: {
    currentParticipants: Participant[];
    selectedParticipants: Participant[];
    timeslot: Timeslot | undefined;
    onParticipantsSelect: (selected: Participant[]) => void;
    onTimeslotSelected: (timeslot: Timeslot | undefined) => void;
    className: string;
  }) => (
    <div className={config.className}>
      <div className='OptionSelectorBooking_participants'>
        <div className='OptionSelectorBooking_sectionTitle'>
          <FormattedMessage
            defaultMessage='Select or deselect participants:'
            id='selectOrDeselectParticipants'
          />
        </div>
        <ParticipantsPicker
          participants={config.currentParticipants}
          selected={config.selectedParticipants}
          participantMode={participantMode}
          onParticipantsSelect={config.onParticipantsSelect}
        />
      </div>

      <DateSelector
        product={product}
        selectedDate={date}
        selectedTimeslot={config.timeslot}
        currency={currency}
        includeDates={includeDates}
        onTimeslotSelected={config.onTimeslotSelected}
        participantsCount={config.selectedParticipants?.length ?? 0}
        disableAutoCollapse
        hideCalendar={hasSingleBookingDay}
      />
    </div>
  );

  return (
    <>
      <OptionSelectorContainer
        option={option}
        currency={currency}
        isOptionSelected={isOptionSelected || isEditingInline}
        isMandatory={isMandatory}
        selectedCount={participantsWithOption.length}
        summary={effectiveSummary}
        onAddAll={handleAddAll}
        onRemoveAll={handleRemoveAll}
        onEdit={handleOpenModal}
      >
        {inlineBooking && (
          <Collapse isOpen={isInlineContentVisible}>
            {renderBookingContent({
              className: 'OptionSelectorBooking_inlineContent',
              currentParticipants: participants,
              onParticipantsSelect: handleInlineSelectParticipants,
              onTimeslotSelected: handleInlineSelectTimeslot,
              selectedParticipants: participantsWithOption,
              timeslot: selectedTimeslot,
            })}
          </Collapse>
        )}
      </OptionSelectorContainer>

      {!inlineBooking && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            <span className='OptionSelectorBooking_modalTitle'>
              <FormattedMessage id='addOption' defaultMessage='Add option' />{' '}
              <span className='OptionSelectorBooking_modalTitle_optionName'>{option.name}</span>
            </span>
          }
          portalProps={{ target: findModalPortal() }}
          fullScreen={isMobile}
          centered={!isMobile}
          size={Size.L}
          className='OptionSelectorBooking_modal'
        >
          <Modal.Body>
            {renderBookingContent({
              className: 'OptionSelectorBooking_modalContent',
              currentParticipants: localParticipants,
              onParticipantsSelect: handleSelectParticipants,
              onTimeslotSelected: handleSelectTimeslot,
              selectedParticipants: localParticipantsWithOption,
              timeslot: localTimeslot,
            })}
          </Modal.Body>
          <Modal.Footer>
            <Button
              type='button'
              onClick={handleCloseModal}
              size={Size.M}
              fullWidth={isMobile}
              variant={ButtonVariant.Outlined}
            >
              <FormattedMessage id='cancel' defaultMessage='Cancel' />
            </Button>
            <Button
              type='button'
              size={Size.M}
              fullWidth={isMobile}
              onClick={handleConfirm}
              disabled={localParticipantsWithOption.length === 0 || !localTimeslot}
            >
              <FormattedMessage id='confirm' defaultMessage='Confirm' />
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default OptionSelectorBooking;
